import React from 'react';
import { TabsContent } from "@/components/ui/tabs";
import { RoomMatchesTab } from "./tabs/RoomMatchesTab";
import { LineItemMatchesTab } from "./tabs/LineItemMatchesTab";
import { OrganizeTab } from "./tabs/OrganizeTab";
import SummaryDashboard from "./SummaryDashboard";
import { SummaryStats, EnhancedRoomMatch, HydratedMatchGroup, OrganizedRoom } from "./types";
import { ComparisonFilters } from "@/hooks/useComparisonState";
import { RoomDetail } from '@/api/generated/client/models/RoomDetail';
import { ComparisonTabErrorBoundary } from "./tabs/ComparisonTabErrorBoundary";

interface ComparisonContentProps {
    stats: SummaryStats;
    enhancedMatches: EnhancedRoomMatch[];
    hydratedMatchGroups: HydratedMatchGroup[];
    organizedRooms: OrganizedRoom[];
    unmatchedLineItemsA: any[];
    unmatchedLineItemsB: any[];
    unmatchedInsurerRooms: OrganizedRoom[];
    unmatchedContractorRooms: OrganizedRoom[];
    allRoomsList: RoomDetail[];
    insurerEstimateId?: number;
    contractorEstimateId?: number;
    filters: ComparisonFilters;
    selectedItemIds: Set<number>;
    selectedRoomIds: Set<number>;
    onApproveMatch: (id: number) => void;
    onRejectMatch: (id: number) => void;
    onUpdateMatch: (data: any) => void;
    onCreateMatch: (data: any) => void;
    onToggleRoomSelection: (id: number) => void;
    onClearRoomSelection: () => void;
    onToggleItemSelection: (id: number) => void;
    onMoveItems: (targetRoomId: number) => void;
    onDeleteRoom: (roomId: number) => void;
    onCreateRoom: (estimateId: number, name: string) => void;
    onMoveLineItem: (itemId: number, targetRoomId?: number | null, targetLineItemMatchGroupId?: number | null) => void;
    onSetItemsSelection: (itemIds: number[], isSelected: boolean) => void;
    onTriggerCreateRoom: (type: 'insurer' | 'contractor') => void;
    onNavigateToMatch: (index: number) => void;
    onRematchRoom: (roomId: number) => void;
    onApproveLineItemMatchGroup: (groupId: number) => void;
    onRejectLineItemMatchGroup: (groupId: number) => void;
    onCreateLineItemMatchGroup: (insurerLineItemIds: number[], contractorLineItemIds: number[], roomMatchGroupId: number | null) => void;
    onUpdateLineItemMatchGroup: (itemId: number, lineItemMatchGroupId: number | null) => void;
    onDeleteLineItem: (itemId: number) => void;
    comparisonId: number;
    canWrite: boolean;
    // New Filter Props
    overscopedIds?: Set<number>;
    additionalIds?: Set<number>;
    discrepancyIds?: Set<number>;
    notOnInsuranceIds?: Set<number>;
    toggleShowOverscoped?: () => void;
    toggleShowAdditional?: () => void;
    toggleShowDiscrepancy?: () => void;
    toggleShowNotOnInsurance?: () => void;
    applySingleFilter?: (filter: 'overscoped' | 'additional' | 'discrepancy' | 'notOnInsurance') => void;
    setActiveTab?: (tab: string) => void;
}

export function ComparisonContent({
    stats,
    enhancedMatches,
    hydratedMatchGroups,
    organizedRooms,
    unmatchedLineItemsA,
    unmatchedLineItemsB,
    unmatchedInsurerRooms,
    unmatchedContractorRooms,
    allRoomsList,
    insurerEstimateId,
    contractorEstimateId,
    filters,
    selectedItemIds,
    selectedRoomIds,
    onApproveMatch,
    onRejectMatch,
    onUpdateMatch,
    onCreateMatch,
    onToggleRoomSelection,
    onClearRoomSelection,
    onToggleItemSelection,
    onMoveItems,
    onDeleteRoom,
    onMoveLineItem,
    onSetItemsSelection,
    onTriggerCreateRoom,
    onNavigateToMatch,
    onRematchRoom,
    onApproveLineItemMatchGroup,
    onRejectLineItemMatchGroup,
    onCreateLineItemMatchGroup,
    onUpdateLineItemMatchGroup,
    onDeleteLineItem,
    comparisonId,
    canWrite,
    // New Props
    overscopedIds,
    additionalIds,
    discrepancyIds,
    notOnInsuranceIds,
    toggleShowOverscoped,
    toggleShowAdditional,
    toggleShowDiscrepancy,
    toggleShowNotOnInsurance,
    applySingleFilter,
    setActiveTab,
}: ComparisonContentProps) {

    const handleViewOverscoped = () => {
        applySingleFilter?.('overscoped');
        setActiveTab?.('organize');
    };

    const handleViewAdditional = () => {
        applySingleFilter?.('additional');
        setActiveTab?.('organize');
    };

    const handleViewDiscrepancy = () => {
        applySingleFilter?.('discrepancy');
        setActiveTab?.('organize');
    };

    const handleViewNotOnInsurance = () => {
        applySingleFilter?.('notOnInsurance');
        setActiveTab?.('organize');
    };

    return (
        <div className="flex-1 pt-4 w-full">
            <TabsContent value="matches" className="m-0 focus-visible:ring-0">
                <ComparisonTabErrorBoundary tabName="Room Matches">
                    <RoomMatchesTab
                        matches={enhancedMatches}
                        filters={filters}
                        onApprove={onApproveMatch}
                        onReject={onRejectMatch}
                        onUpdateMatch={onUpdateMatch}
                        onCreateMatch={onCreateMatch}
                        unmatchedInsurerRooms={unmatchedInsurerRooms}
                        unmatchedContractorRooms={unmatchedContractorRooms}
                        selectedRoomIds={selectedRoomIds}
                        onToggleRoomSelection={onToggleRoomSelection}
                        onClearRoomSelection={onClearRoomSelection}
                        onRematchRoom={onRematchRoom}
                    />
                </ComparisonTabErrorBoundary>
            </TabsContent>

            <TabsContent value="items" className="m-0 focus-visible:ring-0">
                <ComparisonTabErrorBoundary tabName="Line Item Matches">
                    <LineItemMatchesTab
                        groups={hydratedMatchGroups}
                        roomMatches={enhancedMatches}
                        filters={filters}
                        onApprove={onApproveLineItemMatchGroup}
                        onReject={onRejectLineItemMatchGroup}
                        onRematchRoom={onRematchRoom}
                        onMoveLineItem={(itemId, groupId) => onMoveLineItem(itemId, undefined, groupId)}
                        onCreateGroup={onCreateLineItemMatchGroup}
                        onUpdateLineItemMatchGroup={onUpdateLineItemMatchGroup}
                        selectedItemIds={selectedItemIds}
                        onToggleItemSelection={onToggleItemSelection}
                        onClearSelection={onClearRoomSelection}
                    />
                </ComparisonTabErrorBoundary>
            </TabsContent>

            <TabsContent value="organize" className="m-0 focus-visible:ring-0">
                <ComparisonTabErrorBoundary tabName="Organize">
                    <OrganizeTab
                        rooms={organizedRooms}
                        filters={filters}
                        selectedItemIds={selectedItemIds}
                        onToggleItemSelection={onToggleItemSelection}
                        onMoveItems={onMoveItems}
                        onDeleteRoom={onDeleteRoom}
                        allRoomsList={allRoomsList}
                        insurerEstimateId={insurerEstimateId}
                        contractorEstimateId={contractorEstimateId}
                        onMoveLineItem={onMoveLineItem}
                        onSetItemsSelection={onSetItemsSelection}
                        onTriggerCreateRoom={onTriggerCreateRoom}
                        onDeleteLineItem={onDeleteLineItem}
                        comparisonId={comparisonId}
                        canWrite={canWrite}
                        // New Props
                        overscopedIds={overscopedIds}
                        additionalIds={additionalIds}
                        discrepancyIds={discrepancyIds}
                        notOnInsuranceIds={notOnInsuranceIds}
                        toggleShowOverscoped={toggleShowOverscoped}
                        toggleShowAdditional={toggleShowAdditional}
                        toggleShowDiscrepancy={toggleShowDiscrepancy}
                        toggleShowNotOnInsurance={toggleShowNotOnInsurance}
                    />
                </ComparisonTabErrorBoundary>
            </TabsContent>

            <TabsContent value="summary" className="m-0 focus-visible:ring-0">
                <ComparisonTabErrorBoundary tabName="Summary">
                    <SummaryDashboard
                        stats={stats}
                        enhancedMatches={enhancedMatches}
                        unmatchedLineItemsA={unmatchedLineItemsA}
                        unmatchedLineItemsB={unmatchedLineItemsB}
                        onNavigateToMatch={onNavigateToMatch}
                        // New Handlers
                        onViewOverscopedItems={handleViewOverscoped}
                        onViewAdditionalItems={handleViewAdditional}
                        onViewUnderscopedItems={handleViewDiscrepancy}
                        onViewOmittedItems={handleViewNotOnInsurance}
                    />
                </ComparisonTabErrorBoundary>
            </TabsContent>
        </div>
    );
}
